import dayjs from 'dayjs';
import { useState } from 'react';
import { useField, useFormikContext } from 'formik';
import Box from '@mui/material/Box';
import { useTheme } from "@mui/material";
import FormAlert from '../Alert/FormAlert';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const DISPLAY_FORMAT = "YYYY-MM-DD";

const DateInput = ({ label, ...props }) => {
  const theme = useTheme();
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();
  const [showError, setShowError] = useState(true);

  const handleChange = (value) => {
    const formatted = value ? dayjs(value).format(DISPLAY_FORMAT) : '';
    setFieldValue(field.name, formatted);
  };

  // ✅ Extracted as a shared constant — identical to TextInput's sx
  const textFieldSx = {
    "& .MuiInputBase-input": {
      color: theme.palette.mode === 'dark'
        ? theme.palette.grey[100]
        : theme.palette.grey[900],
      fontWeight: 400,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.grey[900],   // ← was theme.palette.text.secondary
      fontWeight: 400,
      fontSize: '0.8rem',
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.grey[900],   // ← was missing entirely
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.default,
      transition: "border-color 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.background.default,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.grey[900],
        transition: "border-color 0.2s ease",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.grey[900],
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.grey[900],
        borderWidth: 2,
      },
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.background.default,
      boxShadow: "none",
    },
    "& input": {
      backgroundColor: "transparent",
      "&:-webkit-autofill": {
        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset !important`,
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          width: "100%",
          "& .MuiTextField-root": { mt: 2, width: "100%" },
        }}
      >
        <DatePicker
          label={label}
          value={field.value ? dayjs(field.value) : null}
          onChange={handleChange}
          format={DISPLAY_FORMAT}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              margin: "normal",
              error: meta.touched && Boolean(meta.error),
              helperText: meta.touched && meta.error ? meta.error : '',
              sx: textFieldSx,  // ← drop-in replacement
            },
            popper: {
              sx: {
                '& .MuiPaper-root': {
                  backgroundColor: theme.palette.grey.C200,
                  borderRadius: 6,
                },
                '& .MuiPickersCalendarHeader-root': {
                  backgroundColor: theme.palette.grey.C300,
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  color: theme.palette.common.black,
                  fontWeight: 600,
                },
                '& .MuiPickersDay-root': {
                  color: theme.palette.greydark.C900,
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#bbdefb' },
                },
                '& .MuiPickersDay-root.Mui-selected': {
                  backgroundColor: theme.palette.grey.C500,
                  '&:hover': { backgroundColor: theme.palette.greydark.C900 },
                },
                '& .MuiPickersYear-yearButton': {
                  color: '#000',
                  '&.Mui-selected': { backgroundColor: theme.palette.greydark.C900 },
                },
              },
            },
          }}
        />
      </Box>

      {meta.touched && meta.error && showError && (
        <FormAlert
          severity="warning"
          message={meta.error}
          onClose={() => setShowError(false)}
        />
      )}
    </LocalizationProvider>
  );
};

export default DateInput;